import { SelectedStatus } from "@b2bportal/core-types";
import {
  combineTrackingProperties,
  type ITrackingPropertiesRequest,
} from "@hopper-b2b/utilities";
import { createSelector } from "@reduxjs/toolkit";
import { slice } from "./slice";

export const selectSliceState = (
  state: Parameters<typeof slice.selectSlice>[0]
) => {
  if (!(slice.reducerPath in state)) {
    console.warn(
      `The slice reducer path "${slice.reducerPath}" was not found in the state. falling back to initial state.`
    );
    return slice.getInitialState();
  }

  return slice.selectSlice(state);
};

export const getFetchCfarOffersCallState = createSelector(
  selectSliceState,
  ({ fetchCfarOffersCallState }) => fetchCfarOffersCallState
);

export const selectFetchCfarOffersRequest = createSelector(
  selectSliceState,
  ({ fetchCfarOffersRequest }) => fetchCfarOffersRequest
);

export const getFetchedCfarOffers = createSelector(
  selectSliceState,
  ({ cfarOffers }) => cfarOffers
);

export const getSelectedCfarOffer = createSelector(
  selectSliceState,
  ({ selectedCfarOffer }) => selectedCfarOffer
);

export const getFetchedCfarOffersTrackingProperties = createSelector(
  getFetchedCfarOffers,
  (cfarOffers) => {
    if (cfarOffers != null) {
      return cfarOffers.map((cfarOffer) => cfarOffer.trackingProperties);
    }
    return undefined;
  }
);

export const getSkipCfarTrackingProperties = createSelector(
  selectSliceState,
  ({ skipCfarTrackingProperties }) => skipCfarTrackingProperties ?? {}
);

enum CfarChoice {
  ACCEPTED = "accepted",
  DECLINED = "declined",
  NOT_OFFERED = "not_offered",
}

const getCfarChoiceOption = createSelector(
  getFetchedCfarOffers,
  getSelectedCfarOffer,
  (cfarOffers, selectedCfarOffer) => {
    if (cfarOffers != null && cfarOffers.length > 0) {
      if (selectedCfarOffer !== undefined && selectedCfarOffer !== null) {
        return CfarChoice.ACCEPTED;
      }
      return CfarChoice.DECLINED;
    }
    return CfarChoice.NOT_OFFERED;
  }
);

export const getChooseCfarTrackingProperties = createSelector(
  getFetchedCfarOffersTrackingProperties,
  getSkipCfarTrackingProperties,
  getCfarChoiceOption,
  (
    fetchedCfarTrackingproperties,
    skipCfarOfferProperties,
    cfarChoiceOption
  ): ITrackingPropertiesRequest => {
    return combineTrackingProperties(
      [...(fetchedCfarTrackingproperties ?? []), skipCfarOfferProperties],
      {
        cfar_choice:
          cfarChoiceOption === CfarChoice.ACCEPTED
            ? true
            : cfarChoiceOption === CfarChoice.DECLINED
              ? false
              : undefined,
      },
      []
    );
  }
);

export const getCfarFareRefundability = createSelector(
  selectSliceState,
  ({ fareRefundability }) => fareRefundability
);

export const getCfarOffersV2TrackingProperties = createSelector(
  selectSliceState,
  ({ fetchCfarOfferTrackingProperties }) => fetchCfarOfferTrackingProperties
);

export const getSkipCfarV2TrackingProperties = createSelector(
  selectSliceState,
  ({ skipCfarTrackingProperties }) => skipCfarTrackingProperties ?? {}
);

const getCfarV2ChoiceOption = createSelector(
  getFetchedCfarOffers,
  getSelectedCfarOffer,
  (cfarOffers, selectedCfarOffer) => {
    if (cfarOffers != null && cfarOffers.length > 0) {
      if (
        selectedCfarOffer !== undefined &&
        selectedCfarOffer !== null &&
        selectedCfarOffer.status === SelectedStatus.SELECTED
      ) {
        return CfarChoice.ACCEPTED;
      }
      return CfarChoice.DECLINED;
    }
    return CfarChoice.NOT_OFFERED;
  }
);

export const getChooseCfarV2TrackingProperties = createSelector(
  getCfarOffersV2TrackingProperties,
  getSkipCfarV2TrackingProperties,
  getCfarV2ChoiceOption,
  (
    cfarTrackingproperties,
    skipCfarOfferProperties,
    cfarChoiceOption
  ): ITrackingPropertiesRequest => {
    return combineTrackingProperties(
      [cfarTrackingproperties ?? {}, skipCfarOfferProperties],
      {
        cfar_choice:
          cfarChoiceOption === CfarChoice.ACCEPTED
            ? true
            : cfarChoiceOption === CfarChoice.DECLINED
              ? false
              : undefined,
      },
      []
    );
  }
);
